import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import AuthContext from '../../../contexts/AuthContext';
import TokenListContext from '../../../contexts/TokenListContext';
import useAPI from '../../../hooks/useAPI';
import type { IUser } from '../../../types/UserType';
import { formatCurrency } from '../../../utility/number.utils';
import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import FormError from '../../ui/FormError';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import type { IMember, ReceiveTokenModalProps } from './type';

const schema = z.object({
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) > 0, {
      message: 'Total tokens must be a positive number greater than 0',
    }),
  receiveFrom: z.string().min(1, 'Select a member'),
});

type FormData = z.infer<typeof schema>;

const ReceiveTokenModal = ({
  onClose,
  open,
  tokenId,
}: ReceiveTokenModalProps) => {
  const [members, setMembers] = useState<IMember[]>([]);
  const [selectedOption, setSelectedOption] = useState<IMember | null>(null);
  const [showSuccess, setShow] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>();
  const { adminBalance } = useContext(AuthContext);
  const { refetchTransactionData, tokenList } = useContext(TokenListContext);

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const {
    refetch: receiveToken,
    data,
    isLoading: isReceiveLoading,
  } = useAPI({
    url: '/token/admin/receive/cash',
    method: 'POST',
    onSuccess: data => {
      reset();
      setShow(true);
      setTransactionId(data.result.data.data.transactionId);
      onClose(true);
      refetchTransactionData();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const { isLoading, data: membersData } = useAPI({
    url: '/auth/admin/user/list?page=1&limit=10',
    method: 'GET',
    onSuccess: data => {
      const mappedMembers = data?.result?.users.map((user: IUser) => ({
        label: user.firstName + ' ' + user.lastName,
        value: user?.wallet?.[0]?.walletAddress,
      }));
      setMembers(mappedMembers);
      if (mappedMembers?.length > 0) {
        // setSelectedOption(mappedMembers[0]);

        const member = data?.result?.users.find(
          (user: IUser) => user.id === mappedMembers[0]?.value,
        );

        if (member.numberOfToken == 0) {
          setError('receiveFrom', {
            message: "The User Doesn't have any tokens in their wallet",
          });
          return;
        }

        setValue('receiveFrom', mappedMembers[0].value);
      }
    },
  });

  // console.log('🚀 ~ membersData:', membersData);
  const handleSelectChange = (option: IMember | null) => {
    setSelectedOption(option);
    const member = membersData?.result?.users.find(
      (user: IUser) => user?.wallet?.[0]?.walletAddress === option?.value,
    );

    if (member?.numberOfToken == 0) {
      setError('receiveFrom', {
        message: "The User Doesn't have any tokens in their wallet",
      });
    } else {
      clearErrors('receiveFrom');
    }
    if (option) {
      setValue('receiveFrom', option?.value);
    }
  };

  const handleReceiveToken = (data: FormData) => {
    const formDataToSend = new FormData();

    const member = membersData?.result?.users.find(
      (user: IUser) => user?.wallet[0].walletAddress === selectedOption?.value,
    );

    formDataToSend.append('amount', data.amount);
    formDataToSend.append('receiveFrom', data.receiveFrom);
    const payload = {
      receiverWallet: data.receiveFrom,
      tokenId: tokenId,
      amount: data.amount,
    };
    receiveToken(payload);
  };

  if (open && showSuccess)
    return (
      <TransactionSuccessModal
        open={true}
        title="Your token request has been successfully sent. You will be notified once it is processed."
        isCloseIconVisible={true}
        onClose={() => {
          setShow(false);
          onClose(true);
        }}
      />
    );

  return (
    <Modal
      open={open}
      title="Receive Token"
      onClose={onClose}
      isCloseIconVisible={!isReceiveLoading}
      dialogClassName={styles.dialog_content}
    >
      <form onSubmit={handleSubmit(handleReceiveToken)}>
        <div className={styles.receiveWrapper}>
          <div className={styles.receiveInner}>
            <div className={styles.receiveItem}>
              <label>Token Name</label>
              <span>
                {tokenList?.[0]?.tokenName || '-'}(
                {tokenList?.[0]?.tokenSymbol || '-'})
              </span>
            </div>
            <div className={styles.receiveItem}>
              <label>Available Tokens</label>
              <span>
                {formatCurrency(adminBalance?.UserBalance ?? 0, {
                  currency: '',
                  decimals: 0,
                })}
              </span>
            </div>
          </div>
          {/* <Input
            label="Total Token to Receive"
            isRequired={true}
            {...register('amount')}
            value={formData.amount}
            onChange={handleInputChange}
            error={errors.amount?.message as string}
          /> */}
          <Form.Group className="mb-3">
            <Form.Label htmlFor="" className={styles.label}>
              Enter Amount <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={0}
                    allowNegative={false}
                    customInput={Input}
                    onValueChange={value => field.onChange(value.value)}
                    error={errors.amount?.message as string}
                    style={{
                      paddingRight: tokenList?.[0]?.tokenSymbol
                        ? '60px'
                        : '10px',
                    }}
                  />
                  {tokenList?.[0]?.tokenSymbol && (
                    <span
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontWeight: 600,
                        pointerEvents: 'none',
                      }}
                    >
                      {tokenList[0].tokenSymbol}
                    </span>
                  )}
                </div>
              )}
            />
          </Form.Group>
          <Form.Group className={styles.selectGroup}>
            <Form.Label className={styles.selectLabel}>
              Receive Token From <span className={styles.required}>*</span>
            </Form.Label>
            <Select
              name="receiveFrom"
              className={styles.selectBox}
              value={selectedOption}
              placeholder="Select User"
              onChange={handleSelectChange}
              options={members}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  // bac
                  borderColor: state.isFocused ? 'grey' : 'red',
                }),
              }}
            />
            <FormError message={errors.receiveFrom?.message as string} />
          </Form.Group>
        </div>

        <div className={styles.actions}>
          <Button
            variant="outline"
            type="button"
            onClick={() => onClose()}
            disabled={isReceiveLoading}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            type="submit"
            className={styles.delete_btn}
            disabled={
              !selectedOption ||
              !!errors.receiveFrom?.message ||
              isReceiveLoading
            }
          >
            {isReceiveLoading ? <Loader /> : 'Confirm'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ReceiveTokenModal;
